import React from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import _ from 'lodash'

import { Modal, Animation, Button, Icon, Breadcrumbs, RichText, Spinner } from 'components'
import { SortableForest, ItemBlock, ToggleButton, ItemInput, SectionBlock } from 'features'
import { FaTimes, FaFileAlt, FaAngleUp, FaEye, FaEyeSlash } from 'react-icons/fa'
import { AnimatePresence, AnimateSharedLayout, motion } from 'framer-motion'

import { pageContext, sectionContext, itemContext } from 'context'
import { Item } from 'schema'
import * as actionCreators from 'store/actions'
import classes from './ItemModal.module.css'

const Wrapper = styled.div`
    overflow-y: hidden;
`

const Container = styled.div`
    z-index: 1300;
    padding: 2rem 2rem 2rem 2rem;
    border-radius: 5px;
    min-width: 30rem;
    width: 40vw;
    height: 90vh;
    overflow-y: auto;
    overflow-x: hidden;
    background: var(--primary-color);
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    border-radius: 5px;
    box-shadow: 0px 2px 1px -1px var(--primary-accent2-color), 
                0px 1px 1px 0px var(--primary-accent2-color),
                0px 1px 3px 0px var(--primary-accent2-color);
    @media (max-width: 1000px) {
        width: 60vw;
        min-width: 0;
    }
    @media (max-width: 600px) {
        width: 80vw;
        min-width: 0;
    }
`

const NotesWrapper = styled.div`
    max-width: calc(40vw - 4rem);
    @media (max-width: 1000px) {
        max-width: calc(60vw - 4rem);
    };
    @media (max-width: 600px) {
        max-width: calc(80vw - 4rem);
    };
`

const Header = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`

const Main = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
`

const Section = styled.div`
    width: 100%;
    height: 100%;
    display: grid;
    grid-template-rows: min-content 1fr;
`

const SubSection = styled.div`
    width: 100%;
    height: 100%;
    margin: ${props => props.margin || '0 0 1rem 0'};
    flex-grow: ${props => props.flexGrow || null};
    max-height: ${props => props.maxHeight || null};
    display:inline-block;
`

const toggleStyle = {
    backgroundColor: 'transparent',
    padding: '0.3rem', 
    fontSize: '1rem'
}

const showButtonStyle = {
    color:'var(--secondary-accent-color)',
    fontSize:'0.8rem',
    backgroundColor: 'var(--primary-color)',
    padding: '0.3rem',
    margin: '0 0.5rem',
    boxShadowColor: 'transparent',
}

const ItemModal = props => {
    const { 
        onClose,
        onEsc,
        itemID
    } = props
    const debug = false

    ///////////
    // Redux //
    ///////////
    const dispatch = useDispatch()
    const sessionId = useSelector(state => state.content.sessionId)
    const data = useSelector(state => state.db.present.data && state.db.present.data.toJS())
    const user = useSelector(state => state.auth.user)
    const inputID = useSelector(state => state.content.inputID)
    const modalID = useSelector(state => state.content.modalID)
    const [showSpinner, setSpinner] = React.useState(true)
    const item = data['items'][itemID]
    const page = item && data['pages'][item['pageID']]
    const section = item && data['sections'][item['sectionID']]

    React.useEffect(() => {
        dispatch(actionCreators.loadItems(data, {
            "logic": "OR",
            userID: user['uid'],
            "filters": {
                _id: itemID,
                parents: { "$in" : [itemID]}
            }
        }, (values) => setSpinner(false)))
        
    }, [])

    ///////////
    // State //
    ///////////
    const [showNotes, setShowNotes] = React.useState(true)
    const [showTasks, setShowTasks] = React.useState(true)
    const [showCompleted, setShowCompleted] = React.useState(true)

    //////////////
    // Handlers //
    //////////////
    const onCloseModal = () => {
        modalID && dispatch(actionCreators.setModal(null,null))
        onClose && onClose()
    }

    const onChangePage = (value) => {
        const pageID = value['id']
        if(!Object.keys(data['pages']).includes(pageID)){ return }
        const type = value['type']
        onCloseModal()
        item['pageID'] !== user['activePageID'] && this.props.updateUser({...user, activePageID: pageID, activePageType: type})
    }

    const onUpdateHandler = (value, type) => {
        let updatedItem = item
        updatedItem[type] = value
        dispatch(actionCreators.updateItem(updatedItem, data, user['uid'], sessionId))
    }

    const richTextSubmitHandler = (rawData, plainText) => {
        const currentData = item['notes']
        !_.isEqual(currentData, rawData) && onUpdateHandler(rawData, 'notes')
    }

    const onDrop = values => {
        dispatch(actionCreators.commitDND2({...values, data, userId: user['uid'], sessionId}))
    }

    const renderDragOverlay = (id) => {
        return {
            item: <ItemBlock itemID={id.split('_')[0]} type='DRAG_OVERLAY'/>,
            container: <SectionBlock section={data['sections'][id]} type='DRAG_OVERLAY'/>
        }
    }

    const buildView = () => {
        const disableDND = inputID
        const children = item.getFamily(showCompleted, data)
        // const tree = _.sortBy(Object.values(children), ['status']).reverse()
        const tree = [...Object.values(children).filter(child => child['status'] === 'INCOMPLETE'), ...Object.values(children).filter(child => child['status'] === 'COMPLETED')]
                    .map(child => ({
                        id: child['itemID'] && `${child['itemID']}`,
                        parentId: child['parents'] && `${child['parents'].slice(-1)[0]}`,
                        block: <ItemBlock itemID={child['itemID']} disableDrag={item['status'] === itemContext.STATUS.COMPLETED}/>,
                        depth: child['parents'].length - item['parents'].length - 1,
                        disableDrag: inputID || child['status'] === itemContext.STATUS.COMPLETED,
                        parents: child['parents'],
                        containerId: `${itemID}`
                    }))
        const lastItem = Object.values(children).slice(-1)[0]
        const lastParents = !lastItem ? [...item['parents'], item['itemID']] : [...lastItem['parents'], lastItem['itemID']]
        const afterTree = <ItemInput id={itemID + '_ItemInput'}
                            pageID={page['pageID']} sectionID={section['sectionID']} 
                            parents={lastParents}
                            offset={item['parents'].length + 1}  
                            enableTab/>
        const trees = [{
            id: `${itemID}`,
            tree: tree,
            afterTree: afterTree,
            disableDrag: true,
            containerId: page['pageID']
        }]

        debug && console.log('[ItemModal]', trees)
        return <SortableForest key='ITEM_FOREST' id='ITEM_FOREST' 
                    trees={trees} defaultParents={[...item['parents'], item['itemID']]}
                    dragOverlay={renderDragOverlay} onDrop={onDrop} disableVirtual disableDND={disableDND}
                    styles={{treeWidth: '100%', treeHeight: 'auto', margin: '0 0 1rem 0', padding: 0}}/>
    }

    if(!item){
        return (
            <Modal closeModal={onCloseModal} onEsc={() => !inputID && onEsc && onEsc()}>
                <Container>
                    <Spinner styles={{ alignSelf: 'center' }}/>
                </Container>
            </Modal>
        )
    } else {
        return (
            <Modal closeModal={onCloseModal} onEsc={() => !inputID && onEsc && onEsc()}>
                <Container>
                    {/* Header */}
                    <Header>
                        <div className={classes.HeaderPre}>
                            <Icon value={page['icon'] ? page['icon']['value'] : null} 
                                type={page['icon'] ? page['icon']['type'] : null} 
                                defaultIcon={<FaFileAlt/>} 
                                size='1rem'/>
                            <Breadcrumbs crumbs={[
                                {content: page['page_name'], handler: onChangePage, value: {id: item['pageID'], type: pageContext.TYPES.PAGES}},
                                {content: section['section_name'], handler: onChangePage, value: {id: item['pageID'], type: pageContext.TYPES.PAGES}}
                            ]}/>                            
                        </div>
                        <Button icon={<FaTimes/>} onSelect={onCloseModal} tooltip='Close' 
                            styles={{backgroundColor:'var(--primary-color)', boxShadowColor: 'transparent', padding:'0.3rem'}}/>
                    </Header>
                    
                    {/* Item */}
                    <Main>
                        <ItemBlock itemID={item['itemID']}
                            key={`${item['itemID']}_ItemModal`} 
                            onCheck={() => dispatch(actionCreators.setModal(null, null))} 
                            disableBottomBorder disableDrag disableToggle/>
                    </Main>
                    
                    <AnimateSharedLayout>
                        <Section>
                            <SubSection flexGrow={1}>
                                {/* Title */}
                                <motion.div layout='position'>
                                    <div className={classes.SectionTitle} onClick={() => setShowNotes(!showNotes)}>
                                        <ToggleButton open={showNotes} 
                                            styles={toggleStyle} 
                                            icon={<FaAngleUp/>} end={180}/>
                                        <div className={classes.Title}>Notes</div>
                                    </div>
                                </motion.div>
    
                                {/* Content */}
                                <AnimatePresence>
                                    {showNotes && 
                                    <Animation animation='expand' layout='position'>
                                        <NotesWrapper>
                                            <RichText id={item['itemID']} value={item['notes']} key={`${item.itemID}_ItemNotes`}
                                                onSubmit={richTextSubmitHandler} 
                                                controls={true} scroll={true} disableDropdown editable disableClose disableAutoFocus
                                                readOnly={item.status === itemContext.STATUS.COMPLETED || section['status'] === sectionContext.STATUS.ARCHIVE}
                                                placeholder={item.status === itemContext.STATUS.COMPLETED || section['status'] === sectionContext.STATUS.ARCHIVE ? 'No notes created' : 'Select to add some notes'}
                                                styles={{
                                                    primaryColor: 'var(--primary-color)',
                                                    secondaryColor: 'var(--secondary-color)',
                                                    margin: 0,
                                                    padding: 0,
                                                    maxHeight: '25vh',
                                            }}/>
                                        </NotesWrapper>
                                    </Animation>}
                                </AnimatePresence>
                            </SubSection>
    
                            <SubSection flexGrow={4}>
                                {/* Title */}
                                <motion.div layout='position'>
                                    <div className={classes.SectionTitle}>
                                        <ToggleButton open={showTasks} 
                                            onSelect={() => setShowTasks(!showTasks)}
                                            styles={toggleStyle} 
                                            icon={<FaAngleUp/>} end={180}/>
                                        <div className={classes.Title} onClick={() => setShowTasks(!showTasks)}>Sub-tasks</div>
                                        {item && showSpinner ? <Spinner/> :
                                        <Button styles={showButtonStyle}
                                            icon={showCompleted ? <FaEye/> : <FaEyeSlash/>}
                                            tooltip='Toggle completed items'
                                            onSelect={() => setShowCompleted(!showCompleted)}/>
                                        }
                                    </div>
                                </motion.div>
    
                                {/* Content */}
                                <AnimatePresence>
                                    {showTasks && 
                                    <Animation animation='expand' layout='position'>
                                        {buildView()}
                                    </Animation>}
                                </AnimatePresence>
                            </SubSection>
                        </Section>
                    </AnimateSharedLayout>
                    
    
                </Container>
            </Modal>
        )
    }
    
}

export default ItemModal